import { addDays, addWeeks, addMonths, addYears } from 'date-fns';

export const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Giornaliera' },
  { value: 'weekly', label: 'Settimanale' },
  { value: 'biweekly', label: 'Bisettimanale' },
  { value: 'monthly', label: 'Mensile' },
  { value: 'yearly', label: 'Annuale' }
];

export const FREQUENCY_LABELS = FREQUENCY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const resolveDebtType = (rawType, transactionType) => {
  if (rawType === 'credit') {
    return 'credit';
  }
  if (rawType === 'debt') {
    return 'debt';
  }
  return transactionType === 'income' ? 'credit' : 'debt';
};

const SUPPORTED_FREQUENCIES = new Set(FREQUENCY_OPTIONS.map(option => option.value));

export const isSupportedFrequency = (frequency) => SUPPORTED_FREQUENCIES.has(frequency);

export const ensureValidDate = (value) => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error('Invalid date value');
    }
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error('Invalid date value');
    }
    return parsed;
  }

  throw new Error('Unsupported date value');
};

export const calculateNextOccurrenceDate = (currentDate, frequency) => {
  if (!isSupportedFrequency(frequency)) {
    return null;
  }

  const baseDate = ensureValidDate(currentDate);

  switch (frequency) {
    case 'daily':
      return addDays(baseDate, 1);
    case 'weekly':
      return addWeeks(baseDate, 1);
    case 'biweekly':
      return addWeeks(baseDate, 2);
    case 'monthly':
      return addMonths(baseDate, 1);
    case 'yearly':
      return addYears(baseDate, 1);
    default:
      return null;
  }
};

export const sanitizeStoreName = (rawStore, type = 'expense') => {
  const trimmed = (rawStore || '').toString().trim();

  if (trimmed.length > 0) {
    return trimmed;
  }

  return type === 'income' ? 'Entrata ricorrente' : 'Spesa ricorrente';
};

export const buildTransactionDraftFromRecurring = (recurring) => {
  if (!recurring) {
    throw new Error('Recurring transaction not provided');
  }

  const {
    amount,
    category,
    walletId,
    note,
    store,
    type,
    isDebtTransaction,
    debtPersonName,
    debtType
  } = recurring;
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    throw new Error('Recurring transaction amount must be a positive number');
  }

  if (!category || typeof category !== 'string' || category.trim() === '') {
    throw new Error('Recurring transaction category is required');
  }

  if (!walletId || typeof walletId !== 'string' || walletId.trim() === '') {
    throw new Error('Recurring transaction walletId is required');
  }

  const sanitizedStore = sanitizeStoreName(store, type);
  const normalizedNote = note ? note.toString().trim() : '';
  const normalizedCategory = category.trim();
  const normalizedWallet = walletId.trim();
  const shouldIncludeDebt = Boolean(isDebtTransaction);
  const trimmedPersonName = typeof debtPersonName === 'string'
    ? debtPersonName.trim()
    : '';

  if (shouldIncludeDebt && !trimmedPersonName) {
    throw new Error('Recurring debt transaction requires a person name');
  }

  const draft = {
    amount: numericAmount,
    category: normalizedCategory,
    walletId: normalizedWallet,
    store: sanitizedStore,
    note: normalizedNote,
    isDebtTransaction: shouldIncludeDebt
  };

  if (shouldIncludeDebt) {
    draft.debtPersonName = trimmedPersonName;
    draft.debtType = resolveDebtType(debtType, type);
  }

  return draft;
};
